//@flow
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {IconButton, makeStyles, TableCell} from '@material-ui/core';
import ActiveCheckBox from '../../../ActiveCheckbox';
import {
  useProductContext,
  // useSetEnabledBulkPriceSettingContext,
} from '../../../../context';
import {Edit} from '@material-ui/icons';
import CustomModal from '../../../../../../elements/CustomModal/CustomModal';
import WholeSaleModal from './WholeSaleModal';
import {useEditProduct} from '../../../../utils/editProduct';

import {useDebounceEffect} from '../../../../../../utils/react';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  wholesalePrice: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '140px',
  },
}));

type BulkPriceLine = {
  minQuantity: number,
  unitPrice: string,
};

export default function WholesalePriceCell() {
  const classes = useStyles();

  const firstRenderFlag = useRef(true);

  const [product, setProduct] = useProductContext(product => ({
    id: product.id,
    bulkPriceSetting: product.bulkPriceSetting,
    taxes: product.taxes ?? [],
    categoryTaxes: product.categoryTaxes ?? [],
  }));

  const {editProduct} = useEditProduct(product ? product.id : '');

  const [enabled, setEnabled] = useState(
    product.bulkPriceSetting && product.bulkPriceSetting.enabled
  );

  const productTaxFactor = useMemo(() => {
    return [...product.taxes, ...product.categoryTaxes]
      .map(parseFloat)
      .reduce((acc, curr) => acc + curr, 1);
  }, [product]);

  const [bulkPriceLines, setBulkPriceLines] = useState<BulkPriceLine[]>(() => {
    if (!product.bulkPriceSetting || !product.bulkPriceSetting.bulkPrices)
      return [];

    const bulkPrice = product.bulkPriceSetting.bulkPrices.map(line => ({
      minQuantity: line.minQuantity,
      unitPrice: (parseFloat(line.unitPrice) / productTaxFactor).toFixed(5),
      taxedUnitPrice: line.unitPrice,
    }));

    return bulkPrice.sort((a, b) => a.minQuantity - b.minQuantity);
  });

  const hasPreviousBulkPrice = bulkPriceLines.length > 0;

  const [modalOpen, setModalOpen] = useState(false);
  const openModal = () => setModalOpen(true);
  const closeModal = (prices?: BulkPriceLine[]) => {
    setModalOpen(false);
  };

  const handleSubmit = (prices: BulkPriceLine[]) => {
    closeModal();
    if (prices.length === 0) {
      setEnabled(false);
    }

    if (!hasPreviousBulkPrice && prices.length > 0) {
      setEnabled(true);
    }

    setBulkPriceLines(prices);
  };

  useDebounceEffect(
    () => {
      if (firstRenderFlag.current) {
        firstRenderFlag.current = false;
        return;
      }

      editProduct({
        bulkPriceSetting: {
          enabled,
        },
      });
    },
    750,
    [enabled]
  );

  useEffect(() => {
    const bulkPriceSetting = {
      ...product.bulkPriceSetting,
      enabled,
    };
    setProduct({
      bulkPriceSetting,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const handleChange = checked => {
    if (!hasPreviousBulkPrice) {
      openModal();
      setEnabled(false);
      return;
    }
    setEnabled(checked);
  };

  return (
    <>
      <TableCell align="right">
        <div className={classes.container}>
          <div className={classes.wholesalePrice}>
            <ActiveCheckBox
              checked={enabled}
              onChange={handleChange}
              controlled
            />
          </div>
          <IconButton onClick={openModal}>
            <Edit />
          </IconButton>
        </div>
      </TableCell>
      <CustomModal open={modalOpen} handleCloseModal={closeModal}>
        {modalOpen && (
          <WholeSaleModal
            onSubmit={handleSubmit}
            bulkPrices={bulkPriceLines}
            taxFactor={productTaxFactor}
          />
        )}
      </CustomModal>
    </>
  );
}
